import { _js } from '@ifixit/utils/localize';
import { Badge } from '@ifixit/ui/components/badge';
import { FaIcon } from '@ifixit/ui/components/icon';
import { imgSrcSet } from 'Guide/image_srcset';
import type { Guide } from './types';

/**
 * The guide's start screen, shown before the reader enters the flow: the hero
 * photo with the title, what the repair involves, and the way in.
 */
export function TitleScreen({ guide, onStart }: { guide: Guide; onStart: () => void }) {
   const image = imgSrcSet(guide.image, '100vw', 'huge');
   const isIfixit = document.documentElement.dataset.site === 'ifixit';
   const guideUrl = guide.url ?? '#';
   const stepCount = guide.steps.length;

   return (
      // Starts below the app's status bar, which draws over the page and would
      // sit on the logo.
      <section className="absolute inset-x-0 bottom-0 top-safe-top overflow-hidden bg-gray-950">
         {image.src && (
            <img
               className="absolute inset-0 size-full object-cover"
               src={image.src}
               srcSet={image.srcSet}
               sizes={image.sizes}
               alt=""
            />
         )}

         {/* Carries the copy below it over an arbitrary photo; without it the
             title sat in an opaque black box across the subject. */}
         <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/10"
         />

         {isIfixit && (
            <a
               href={guideUrl}
               target="_parent"
               className="absolute left-4 top-4 rounded-lg bg-white/90 px-2.5 py-1.5"
            >
               <img
                  src="https://assets.cdn.ifixit.com/static/images/embedv4/ifixit-logo.png"
                  alt="iFixit"
               />
            </a>
         )}

         <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-3 p-5 sm:p-7">
            <h1 className="m-0 line-clamp-2 max-w-[36ch] text-xl font-semibold leading-tight text-white sm:text-2xl">
               <a
                  href={guideUrl}
                  target="_parent"
                  className="text-white no-underline hover:underline"
               >
                  {guide.title}
               </a>
            </h1>

            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
               {stepCount > 0 && (
                  <Badge className="badge-md badge-gray">
                     <FaIcon name="list-ol" aria-hidden />
                     {`${stepCount} ${stepCount === 1 ? _js('Step') : _js('Steps')}`}
                  </Badge>
               )}
               {guide.time_required && (
                  <Badge className="badge-md badge-gray">
                     <FaIcon name="clock" aria-hidden />
                     {guide.time_required}
                  </Badge>
               )}
               {guide.author?.username && (
                  <span>
                     {_js('Author')}:{' '}
                     <span className="font-medium text-white">{guide.author.username}</span>
                  </span>
               )}
            </div>

            <button type="button" onClick={onStart} className="button-primary button-lg mt-1">
               <span>{guide.type === 'teardown' ? _js('View Teardown') : _js('Start Guide')}</span>
               <FaIcon name="play" aria-hidden />
            </button>
         </div>
      </section>
   );
}
